import React, { useState } from 'react';
import { Button, ButtonBar, CreateTableComponent, Dialog } from '@ygtechnologies/rubus-ui';
import { FileUpload, LoadingIndicator, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { ProfileCurrencyDisplay } from '@/seller/components/currency';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
    getCatalogProductImportTemplateUrl,
    importCatalogProductsFromExcel,
    previewCatalogProductImport,
} from '@/services/profile-catalog-products';
import { CatalogProductImportPreviewResult, CatalogProductImportPreviewRow } from '@/types';

const PreviewTable = CreateTableComponent<CatalogProductImportPreviewRow>();

type Props = {
    profileId: number;
    show: boolean;
    onClose: () => void;
    onSave: () => void;
};

export const CatalogProductImportDialog: React.FC<Props> = ({ profileId, show, onClose, onSave }) => {
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<CatalogProductImportPreviewResult | null>(null);
    const [previewing, setPreviewing] = useState(false);
    const [confirming, setConfirming] = useState(false);

    const resetState = () => {
        setFile(null);
        setPreview(null);
        setPreviewing(false);
        setConfirming(false);
    };

    const handleDrop = async (acceptedFiles: File[]) => {
        const dropped = acceptedFiles[0];
        if (!dropped) return;
        if (!dropped.name.toLowerCase().endsWith('.xlsx')) {
            Toast.error(t`Only .xlsx files are supported.`);
            return;
        }
        setFile(dropped);
        setPreview(null);
        setPreviewing(true);
        try {
            setPreview(await previewCatalogProductImport(profileId, dropped));
        } catch {
            Toast.error(t`An error occurred while reading the file.`);
        } finally {
            setPreviewing(false);
        }
    };

    const handleImport = async () => {
        if (!file) return;
        setConfirming(true);
        try {
            const result = await importCatalogProductsFromExcel(profileId, file);
            Toast.success(t`${result.imported.length} products imported successfully.`);
            onSave();
        } catch {
            Toast.error(t`An error occurred during import.`);
        } finally {
            setConfirming(false);
        }
    };

    const hasErrors = preview ? preview.rows.some((r) => r.errors.length > 0) : false;
    const errorCount = preview ? preview.rows.filter((r) => r.errors.length > 0).length : 0;

    return (
        <Dialog
            open={show}
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
            onOpen={resetState}>
            <Dialog.Content contentWidth="lg">
                <Dialog.Header>
                    <Trans>Import products</Trans>
                </Dialog.Header>

                {!preview && (
                    <>
                        <ButtonBar>
                            <Button as="a" href={getCatalogProductImportTemplateUrl()} target="_blank" variant="tertiary">
                                <Trans>Download template</Trans>
                            </Button>
                        </ButtonBar>

                        {previewing ? (
                            <LoadingIndicator />
                        ) : (
                            <FileUpload single onDrop={(acceptedFiles) => handleDrop(Array.from(acceptedFiles))} />
                        )}
                    </>
                )}

                {preview && (
                    <>
                        <PreviewTable data={preview.rows} getRowId={(row) => String(row.rowNumber)} sortingMode="client">
                            <PreviewTable.Column
                                field="rowNumber"
                                header={t`Row`}
                                size={60}
                                renderer={({ row }) => row.rowNumber}
                            />
                            <PreviewTable.Column field="id" header={t`Id`} size={60} renderer={({ row }) => row.id} />
                            <PreviewTable.Column field="name" header={t`Name`} renderer={({ row }) => row.name} />
                            <PreviewTable.Column
                                field="category"
                                header={t`Category`}
                                renderer={({ row }) => row.category}
                            />
                            <PreviewTable.Column
                                field="subcategory"
                                header={t`Subcategory`}
                                renderer={({ row }) => row.subcategory}
                            />
                            <PreviewTable.Column field="type" header={t`Type`} size={100} renderer={({ row }) => row.type} />
                            <PreviewTable.Column
                                field="defaultPrice"
                                header={t`Price`}
                                size={80}
                                renderer={({ row }) => row.defaultPrice != null ? <ProfileCurrencyDisplay value={row.defaultPrice} /> : null}
                            />
                            <PreviewTable.Column
                                field="errors"
                                header={t`Errors`}
                                renderer={({ row }) => <>{row.errors.map((e, i) => <div key={i}>{e}</div>)}</>}
                            />
                        </PreviewTable>

                        {hasErrors && (
                            <p>
                                <Trans>{errorCount} rows have errors. Fix the file and re-upload.</Trans>
                            </p>
                        )}
                    </>
                )}

                <ButtonBar>
                    <ButtonBar.Right>
                        {preview ? (
                            <>
                                <Button variant="tertiary" onClick={() => setPreview(null)} disabled={confirming}>
                                    <Trans>Back</Trans>
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleImport}
                                    loading={confirming}
                                    disabled={hasErrors || confirming}>
                                    <Trans>Import</Trans>
                                </Button>
                            </>
                        ) : (
                            <Button variant="tertiary" onClick={onClose} disabled={previewing}>
                                <Trans>Cancel</Trans>
                            </Button>
                        )}
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};
